/**
 * convertMergeToUpsert — PURE transform that rewrites a convertible MERGE into
 * the connected engine's native upsert (EDG-01/02/03/04). MySQL and SQLite have
 * no MERGE statement at all; PostgreSQL <15 predates it — but all three express
 * this exact "match on key ⇒ update, else insert" intent as a first-class
 * upsert:
 *   - MySQL   → `INSERT INTO t (cols) SELECT … FROM src ON DUPLICATE KEY UPDATE c = VALUES(c)`
 *   - SQLite / PostgreSQL → `INSERT INTO t (cols) SELECT … FROM src ON CONFLICT (keys) DO UPDATE SET c = excluded.c`
 *
 * The transform is deliberately CONSERVATIVE: it only converts the subset it can
 * translate FAITHFULLY (no silent semantic drift — the Wave-0 truthfulness bar).
 * Anything outside that subset returns `{ ok: false, reason }` naming the exact
 * blocking construct, so the caller can teach it and fall back to "Edit as SQL"
 * rather than emit a lie.
 *
 * Convertible subset:
 *   - USING source is a plain table (identifier ± alias) — NOT a subquery /
 *     derived / VALUES source (those need a hand rewrite);
 *   - exactly one WHEN MATCHED → UPDATE branch and exactly one
 *     WHEN NOT MATCHED → INSERT branch (with an explicit column list + VALUES);
 *   - no WHEN MATCHED → DELETE, no WHEN NOT MATCHED BY SOURCE;
 *   - ON is pure column equality (`t.a = s.a [AND t.b = s.b]`) — the conflict
 *     target columns are the target side of those equalities;
 *   - every WHEN MATCHED SET assigns a plain source column that is also one of
 *     the inserted columns, AND that source value is structurally identical to
 *     the INSERT VALUES item aligned with that column, so `excluded.<c>` /
 *     `VALUES(<c>)` is exactly what the MATCHED arm meant (a computed SET like
 *     `q = q + s.n`, or a SET whose value diverges from the aligned INSERT
 *     value like `SET name = s.alt_name` next to `INSERT … VALUES (s.name)`,
 *     can't become a plain upsert assignment and bails).
 *
 * Pure / structural: reuses `parseAssignmentsList` (the SET parser),
 * `insertParseHelpers`, and `tokenSkip` — no regex parsing, no Monaco, no
 * engine round-trip.
 */

import type { ParseOptions, SqlEngine } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import type { MergeOutline, WhenBranch } from '../../render/merge/extractMergeOutline';
import { parseAssignmentsList } from '../../render/insert/parseAssignmentsList';
import {
  IDENT_CONT,
  IDENT_START,
  isWordBoundaryBefore,
  readIdentifier,
  readWord,
  skipWsAndComments,
  splitTopLevelCommasToText,
  stripIdentifierQuotes,
} from '../../render/insert/insertParseHelpers';

export type MergeConvertReason =
  | 'not-a-merge'
  | 'delete-branch'
  | 'not-matched-by-source'
  | 'multiple-matched-branches'
  | 'multiple-not-matched-branches'
  | 'no-update-branch'
  | 'no-insert-branch'
  | 'subquery-source'
  | 'non-equality-on'
  | 'insert-missing-columns'
  | 'matched-set-unsupported'
  | 'matched-set-diverges-from-insert'
  // A surviving matched-update or insert branch carries a `WHEN … AND <cond>`
  // predicate. A plain upsert has no per-row branch condition, so converting it
  // as if unconditional would touch rows the MERGE leaves alone (silent wrong
  // data) — bail to the teaching fallback instead.
  | 'conditional-branch'
  // A participating column identifier (INSERT column list, WHEN MATCHED SET
  // target, or ON key) is quoted. Those tokens are stripped when the upsert is
  // emitted, so a quoted PG `"FirstName"` case-folds ("column does not exist")
  // and a backticked MySQL reserved word becomes a syntax error — bail rather
  // than emit engine-rejected SQL from valid input.
  | 'quoted-identifier';

export type ConvertMergeResult =
  | { readonly ok: true; readonly sql: string }
  | { readonly ok: false; readonly reason: MergeConvertReason };

export function convertMergeToUpsert(
  outline: MergeOutline,
  bufferText: string,
  engine: SqlEngine,
  options: ParseOptions,
): ConvertMergeResult {
  if (
    !outline.hasMerge ||
    outline.targetText === null ||
    outline.sourceText === null ||
    outline.onStart === null ||
    outline.onEnd === null
  ) {
    return fail('not-a-merge');
  }

  // Source must be a plain table — a paren group is a subquery / derived /
  // VALUES source we don't translate in v1.
  if (outline.sourceText.trim().startsWith('(')) return fail('subquery-source');

  const branchResult = classifyBranches(outline.branches);
  if (!branchResult.ok) return branchResult;
  const { matched, insert } = branchResult;

  const insertColumns = insert.action.insertColumns;
  if (
    insertColumns === null ||
    insertColumns.length === 0 ||
    insert.action.valuesStart === null ||
    insert.action.valuesEnd === null
  ) {
    return fail('insert-missing-columns');
  }
  const valuesStart = insert.action.valuesStart;
  const valuesEnd = insert.action.valuesEnd;

  // The emitted upsert STRIPS the INSERT column-list tokens (`readColumnList`
  // already did), so a quoted identifier there would emit case-folded /
  // syntactically-broken SQL — bail. The INSERT column list is the text between
  // the INSERT keyword body and the VALUES paren (identifiers + commas only, so
  // any identifier-quote char is a quoted column).
  if (hasIdentifierQuote(bufferText.slice(insert.action.bodyStart, valuesStart))) {
    return fail('quoted-identifier');
  }

  const target = splitLeadingTable(outline.targetText);
  const keyColumns = extractOnKeyColumns(
    bufferText,
    outline.onStart,
    outline.onEnd,
    options,
    target.alias,
    target.table,
  );
  if (keyColumns === null || keyColumns.length === 0) return fail('non-equality-on');
  // The conflict-target key columns are emitted stripped too. The convertible ON
  // is pure column equality (no string literals), so any identifier-quote char
  // in its span means a quoted key column — bail.
  if (hasIdentifierQuote(bufferText.slice(outline.onStart, outline.onEnd))) {
    return fail('quoted-identifier');
  }

  // WHEN MATCHED SET → upsert assignments. Each SET target must be an inserted
  // column set to a plain source column, so `excluded.<c>` / `VALUES(<c>)` is
  // faithful to what MATCHED meant. `excluded.<c>` / `VALUES(<c>)` resolve to
  // the INSERT's proposed value for `c` — the item of the INSERT VALUES list
  // positionally aligned with `insertColumns[i] === c` — so a SET whose source
  // value differs from that aligned INSERT value (e.g. `SET name = s.alt_name`
  // while INSERT VALUES carries `s.name`) would silently write the wrong value
  // if converted; require provable structural equality first.
  const ctx = skipContextFor(options);
  const insertColSet = new Set(insertColumns.map((c) => stripIdentifierQuotes(c).toLowerCase()));
  const insertValueItems = splitTopLevelCommasToText(bufferText, valuesStart + 1, valuesEnd - 1, ctx);
  const assignments: string[] = [];
  const setRows =
    matched.action.setStart !== null && matched.action.setEnd !== null
      ? parseAssignmentsList(bufferText, matched.action.setStart, matched.action.setEnd, options)
      : [];
  if (setRows.length === 0) return fail('matched-set-unsupported');
  for (const row of setRows) {
    // The SET target column is emitted stripped (`col = excluded.col`), so a
    // quoted target would case-fold / break — bail.
    if (hasIdentifierQuote(row.target)) return fail('quoted-identifier');
    const targetCol = lastSegment(row.target);
    if (targetCol === null) return fail('matched-set-unsupported');
    if (asColumnRef(row.value) === null) return fail('matched-set-unsupported');
    const normalizedTarget = stripIdentifierQuotes(targetCol).toLowerCase();
    if (!insertColSet.has(normalizedTarget)) {
      return fail('matched-set-unsupported');
    }
    const insertColIndex = insertColumns.findIndex(
      (c) => stripIdentifierQuotes(c).toLowerCase() === normalizedTarget,
    );
    const alignedInsertValue = insertColIndex >= 0 ? insertValueItems[insertColIndex] : undefined;
    if (alignedInsertValue === undefined || !exprsStructurallyEqual(row.value, alignedInsertValue, ctx)) {
      return fail('matched-set-diverges-from-insert');
    }
    assignments.push(assignmentFor(engine, stripIdentifierQuotes(targetCol)));
  }

  const columnsText = insertColumns.map((c) => stripIdentifierQuotes(c)).join(', ');
  const valuesText = bufferText.slice(valuesStart + 1, valuesEnd - 1).trim();
  const sourceText = outline.sourceText.trim();

  const insertHead = `INSERT INTO ${target.table} (${columnsText})`;
  const selectClause = `SELECT ${valuesText}\nFROM ${sourceText}`;

  if (engine === 'mysql') {
    // `ON DUPLICATE KEY` is unambiguous after a SELECT — no disambiguator needed.
    // NOTE (I6): ON DUPLICATE KEY UPDATE only *updates* an existing row when a
    // UNIQUE index / PRIMARY KEY covers the ON key columns; without one MySQL
    // silently INSERTs a duplicate instead — the convert CTA carries that caveat
    // (see MergeCapabilityNotice / vqeCopy.capability.merge.convertMysqlKeyCaveat).
    // The `VALUES(col)` reference form is deprecated in MySQL 8.0.20+ (row-alias
    // form is future-proof) but still accepted — left as-is for broad support.
    return {
      ok: true,
      sql: `${insertHead}\n${selectClause}\nON DUPLICATE KEY UPDATE ${assignments.join(', ')}`,
    };
  }

  // SQLite's INSERT…SELECT…UPSERT parser can't tell the trailing `ON CONFLICT`
  // from a JOIN's `ON` clause — its documented workaround is a `WHERE true` on
  // the SELECT. Harmless on PostgreSQL (which has no such ambiguity), so both
  // ON-CONFLICT engines share one form.
  return {
    ok: true,
    sql:
      `${insertHead}\n${selectClause}\nWHERE true\n` +
      `ON CONFLICT (${keyColumns.join(', ')}) DO UPDATE SET ${assignments.join(', ')}`,
  };
}

function fail(reason: MergeConvertReason): { readonly ok: false; readonly reason: MergeConvertReason } {
  return { ok: false, reason };
}

function assignmentFor(engine: SqlEngine, column: string): string {
  return engine === 'mysql'
    ? `${column} = VALUES(${column})`
    : `${column} = excluded.${column}`;
}

interface ClassifiedBranches {
  readonly ok: true;
  readonly matched: WhenBranch;
  readonly insert: WhenBranch;
}

function classifyBranches(
  branches: readonly WhenBranch[],
): ClassifiedBranches | { readonly ok: false; readonly reason: MergeConvertReason } {
  const matched: WhenBranch[] = [];
  const notMatched: WhenBranch[] = [];
  for (const branch of branches) {
    if (branch.matchKind === 'not-matched-by-source') return fail('not-matched-by-source');
    if (branch.matchKind === 'matched') {
      if (branch.action.kind === 'delete') return fail('delete-branch');
      matched.push(branch);
    } else {
      notMatched.push(branch);
    }
  }
  if (matched.length > 1) return fail('multiple-matched-branches');
  if (notMatched.length > 1) return fail('multiple-not-matched-branches');
  const matchedUpdate = matched.find((b) => b.action.kind === 'update');
  if (!matchedUpdate) return fail('no-update-branch');
  const insertBranch = notMatched.find((b) => b.action.kind === 'insert');
  if (!insertBranch) return fail('no-insert-branch');
  // A surviving single branch can still carry a `WHEN … AND <cond>` predicate
  // (`WhenBranch.conditionText`). An upsert has no per-branch condition, so
  // emitting it as unconditional would over-update / drop the insert filter —
  // bail rather than lie.
  if (matchedUpdate.conditionText !== null) return fail('conditional-branch');
  if (insertBranch.conditionText !== null) return fail('conditional-branch');
  return { ok: true, matched: matchedUpdate, insert: insertBranch };
}

/**
 * True when `text` contains an identifier-quote character (`"` / backtick /
 * `[` / `]`). In the convertible subset — column lists, pure column-equality
 * ON, plain `col = source_col` SET targets — these never appear as string
 * literals, so any occurrence is a quoted identifier.
 */
function hasIdentifierQuote(text: string): boolean {
  return /["`[\]]/.test(text);
}

/** Leading (possibly dotted, possibly quoted) table identifier + its alias. */
function splitLeadingTable(text: string): { table: string; alias: string | null } {
  const end = text.length;
  const start = skipWsAndComments(text, 0, end);
  const first = readIdentifier(text, start, end);
  if (!first) return { table: text.trim(), alias: null };
  let j = first.end;
  while (j < end && text[j] === '.') {
    const next = readIdentifier(text, j + 1, end);
    if (!next) break;
    j = next.end;
  }
  const table = text.slice(first.start, j);
  const afterWs = skipWsAndComments(text, j, end);
  const probe = readWord(text, afterWs, end);
  if (!probe) return { table, alias: null };
  if (probe.upper === 'AS') {
    const ws = skipWsAndComments(text, probe.end, end);
    const aliasIdent = readIdentifier(text, ws, end);
    return { table, alias: aliasIdent ? text.slice(aliasIdent.start, aliasIdent.end) : null };
  }
  return { table, alias: text.slice(probe.start, probe.end) };
}

interface ColumnRef {
  readonly qualifier: string | null;
  readonly column: string;
}

/** Parse `ident` or `qualifier.ident` (quoting-aware) spanning the WHOLE text. */
function asColumnRef(raw: string): ColumnRef | null {
  const text = raw.trim();
  const end = text.length;
  if (end === 0) return null;
  const first = readIdentifier(text, 0, end);
  if (!first || first.start !== 0) return null;
  if (first.end === end) {
    return { qualifier: null, column: stripIdentifierQuotes(text.slice(0, end)) };
  }
  if (text[first.end] !== '.') return null;
  const second = readIdentifier(text, first.end + 1, end);
  if (!second || second.end !== end) return null;
  return {
    qualifier: stripIdentifierQuotes(text.slice(first.start, first.end)),
    column: stripIdentifierQuotes(text.slice(second.start, second.end)),
  };
}

/** Last dotted segment of an assignment target (`t.a` → `a`, `a` → `a`). */
function lastSegment(raw: string): string | null {
  const ref = asColumnRef(raw);
  return ref ? ref.column : null;
}

/**
 * Structural equality for two SQL expression fragments (a WHEN MATCHED SET's
 * source value vs. the INSERT's aligned VALUES item). Tokenizes each side —
 * quoted identifiers and string literals compared byte-for-byte, unquoted
 * identifiers case-folded, comments dropped, whitespace collapsed — then
 * compares the token streams. Deliberately conservative: this proves the two
 * fragments are the SAME expression written the same way; anything it can't
 * match (different column, different literal, different shape) compares
 * unequal rather than risk a false "equal" that would silently change the
 * MATCHED semantics.
 */
function exprsStructurallyEqual(a: string, b: string, ctx: SkipContext): boolean {
  return normalizeForCompare(a, ctx) === normalizeForCompare(b, ctx);
}

function normalizeForCompare(raw: string, ctx: SkipContext): string {
  const source = raw.trim();
  const end = source.length;
  const tokens: string[] = [];
  let i = 0;
  while (i < end) {
    const c = source[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    const ident = readIdentifier(source, i, end);
    if (ident) {
      const rawIdent = source.slice(ident.start, ident.end);
      const isQuoted = rawIdent[0] === '"' || rawIdent[0] === '`' || rawIdent[0] === '[';
      tokens.push(isQuoted ? rawIdent : rawIdent.toLowerCase());
      i = ident.end;
      continue;
    }
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      const isLineComment = c === '-' && source[i + 1] === '-';
      const isHashComment = ctx.supportsHashComment && c === '#';
      const isBlockComment = c === '/' && source[i + 1] === '*';
      // Comments are insignificant and dropped; everything else `trySkipNonCode`
      // recognizes here (string literals, postgres dollar-quoting) is real
      // content — kept verbatim, case-sensitive.
      if (!isLineComment && !isHashComment && !isBlockComment) {
        tokens.push(source.slice(i, skipped));
      }
      i = skipped;
      continue;
    }
    if (/[0-9]/.test(c)) {
      let j = i + 1;
      while (j < end && /[0-9.]/.test(source[j])) j++;
      tokens.push(source.slice(i, j));
      i = j;
      continue;
    }
    tokens.push(c);
    i++;
  }
  return tokens.join(' ');
}

function equalsIgnoreCase(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false;
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Target-side columns of the ON predicate. Returns null when any conjunct is
 * not a pure column-to-column equality (so the caller bails `non-equality-on`).
 */
function extractOnKeyColumns(
  source: string,
  onStart: number,
  onEnd: number,
  options: ParseOptions,
  targetAlias: string | null,
  targetTable: string,
): string[] | null {
  const ctx = skipContextFor(options);
  const conjuncts = splitTopLevelAnd(source, onStart, onEnd, ctx);
  const strippedTable = stripIdentifierQuotes(targetTable);
  const strippedAlias = targetAlias ? stripIdentifierQuotes(targetAlias) : null;
  const out: string[] = [];
  for (const c of conjuncts) {
    const eq = findTopLevelEquals(source, c.start, c.end, ctx);
    if (!eq) return null;
    const lhs = asColumnRef(source.slice(c.start, eq.start));
    const rhs = asColumnRef(source.slice(eq.end, c.end));
    if (!lhs || !rhs) return null;
    const lIsTarget =
      equalsIgnoreCase(lhs.qualifier, strippedAlias) || equalsIgnoreCase(lhs.qualifier, strippedTable);
    const rIsTarget =
      equalsIgnoreCase(rhs.qualifier, strippedAlias) || equalsIgnoreCase(rhs.qualifier, strippedTable);
    // Prefer the side qualified with the target; when neither is (bare
    // columns), default to the left column.
    out.push(rIsTarget && !lIsTarget ? rhs.column : lhs.column);
  }
  return out.length > 0 ? out : null;
}

interface Span {
  readonly start: number;
  readonly end: number;
}

/** Split a boolean expression at top-level `AND` keywords (quoting/paren aware). */
function splitTopLevelAnd(source: string, from: number, end: number, ctx: SkipContext): Span[] {
  const out: Span[] = [];
  let segStart = from;
  let i = from;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      i = skipped;
      continue;
    }
    const ch = source[i];
    if (ch === '(') {
      paren++;
      i++;
      continue;
    }
    if (ch === ')') {
      if (paren > 0) paren--;
      i++;
      continue;
    }
    if (paren === 0 && (ch === 'A' || ch === 'a') && isWordBoundaryBefore(source, i) && IDENT_START.test(ch)) {
      let j = i + 1;
      while (j < end && IDENT_CONT.test(source[j])) j++;
      if (source.slice(i, j).toUpperCase() === 'AND') {
        out.push({ start: segStart, end: i });
        segStart = j;
        i = j;
        continue;
      }
      i = j;
      continue;
    }
    i++;
  }
  out.push({ start: segStart, end });
  return out;
}

/**
 * First top-level `=` / `==` equality operator. Returns null when the conjunct
 * has no equality (`>`, `<`, `<>`, `!=`, `>=`, `<=`) — the caller then bails
 * `non-equality-on`.
 */
function findTopLevelEquals(source: string, from: number, end: number, ctx: SkipContext): Span | null {
  let i = from;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      i = skipped;
      continue;
    }
    const ch = source[i];
    if (ch === '(') {
      paren++;
      i++;
      continue;
    }
    if (ch === ')') {
      if (paren > 0) paren--;
      i++;
      continue;
    }
    if (paren === 0 && ch === '=') {
      const prev = source[i - 1];
      // `<=`, `>=`, `!=` are NOT equality — bail the whole predicate.
      if (prev === '<' || prev === '>' || prev === '!') return null;
      const next = source[i + 1];
      return { start: i, end: next === '=' ? i + 2 : i + 1 };
    }
    i++;
  }
  return null;
}
